import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { unlockAccount } from '@brands/services/auth/unlockAccount';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';

export interface UseUnlockAccount {
  showUnlockAccount: boolean;
  isUnlocking: boolean;
  unlockConfirmOpen: boolean;
  openUnlockConfirm: () => void;
  closeUnlockConfirm: () => void;
  handleUnlockAccount: () => Promise<void>;
}

// Shared account-unlock action used by the admin "review profile" headers for every
// user type (patient, admin, provider, care advisor, collector). The unlock endpoint is
// restricted to Admin/SuperAdmin server-side, so mirror that gate in the UI.
const useUnlockAccount = (
  thisUser: UserProfile | undefined,
  setThisUser?: Dispatch<SetStateAction<UserProfile>>,
): UseUnlockAccount => {
  const { userInfo } = useAppSelector(selectAuth);
  const [unlockConfirmOpen, setUnlockConfirmOpen] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  // The user the open confirmation dialog was opened for. The same review-profile component
  // can be re-pointed at a different user (browser history) while the dialog is open, so we
  // pin the target and dismiss the dialog if the displayed profile changes out from under it.
  const confirmTargetId = useRef<number | undefined>(undefined);

  const canUnlockAccount = userInfo.role.name === UserRoleName.Admin || userInfo.role.name === UserRoleName.SuperAdmin;

  // Only surface the action when the account is actually locked (`is_locked` reflects the
  // active lockout window); there is nothing to unlock otherwise.
  const showUnlockAccount = canUnlockAccount && Boolean(thisUser?.is_locked);

  // If the displayed profile switches while the confirmation is open (and no request is in
  // flight), close it so a dialog opened for one user can never submit against another.
  useEffect(() => {
    if (unlockConfirmOpen && !isUnlocking && thisUser?.id !== confirmTargetId.current) {
      setUnlockConfirmOpen(false);
    }
  }, [thisUser?.id, unlockConfirmOpen, isUnlocking]);

  const handleUnlockAccount = async (): Promise<void> => {
    // Capture the target up front: the same review-profile component can be pointed at a
    // different user mid-request (browser history), so bind the request and the local state
    // update to who we actually unlocked rather than to whoever `thisUser` is when it resolves.
    const target = thisUser;
    if (!target?.email) {
      toast.error('This account has no email address to unlock.', { className: 'fs-unmask' });
      setUnlockConfirmOpen(false);
      return;
    }
    // Bind the submit to the user the dialog was opened for: if the displayed profile changed
    // out from under an open dialog, refuse rather than unlock the wrong account (the passive
    // close effect handles this too, but this closes the intervening render synchronously).
    if (confirmTargetId.current !== undefined && target.id !== confirmTargetId.current) {
      setUnlockConfirmOpen(false);
      return;
    }
    setIsUnlocking(true);
    try {
      const response = await unlockAccount({ email: target.email });
      toast.success(response?.message || 'Account unlocked successfully.', { className: 'fs-unmask' });
      // Clear the locked flag locally so the action hides itself without a page refresh
      // and cannot fire a redundant unlock request against an already-unlocked account. Guard
      // on id so a mid-request profile switch doesn't clear a different user's lock state.
      setThisUser?.((prev) => (prev.id === target.id ? { ...prev, is_locked: false } : prev));
      setUnlockConfirmOpen(false);
    } catch (error: unknown) {
      // Surfaces the API's not-found / failure message (e.g. account email not found) to the admin.
      displayErrorDetails(error);
    } finally {
      setIsUnlocking(false);
    }
  };

  return {
    showUnlockAccount,
    isUnlocking,
    unlockConfirmOpen,
    openUnlockConfirm: () => {
      confirmTargetId.current = thisUser?.id;
      setUnlockConfirmOpen(true);
    },
    closeUnlockConfirm: () => {
      if (!isUnlocking) setUnlockConfirmOpen(false);
    },
    handleUnlockAccount,
  };
};

export default useUnlockAccount;
